import { useState, type ReactNode } from 'react'
import { ExternalLink } from 'lucide-react'
import { Button, CopyField, SegmentedControl, TooltipProvider } from '@teach/ui'
import butterfly from '@/assets/butterfly.svg'
import { ScrollFadeRow } from '@/components/common/ScrollFadeRow'
import { useIsMobile } from '@/hooks/use-mobile'
import { useMessages, useUiLang } from '@/i18n'
import { CLIENTS, MCP_URL, type ClientId } from '../guideContent'
import { guideFor } from '../guideMessages'
import { messages } from '../messages'
import { AntigravityIcon, ChatGptIcon, ClaudeCodeIcon, ClaudeIcon, HermesIcon } from './BrandIcons'
import { ClaudeCodePromptCard } from './ClaudeCodePromptCard'
import { ClaudeConnectButton } from './ClaudeConnectButton'
import { Faq } from './Faq'
import { GuideHeading } from './GuideHeading'
import { PromptCards } from './PromptCards'

const CLIENT_ICONS = {
  claude: ClaudeIcon,
  code: ClaudeCodeIcon,
  chatgpt: ChatGptIcon,
  antigravity: AntigravityIcon,
  hermes: HermesIcon,
}

const StepLink = ({ href, label }: { href: string; label: string }) => (
  <Button asChild variant="secondary" size="sm">
    <a href={href} target="_blank" rel="noopener noreferrer">
      {label}
      <ExternalLink />
    </a>
  </Button>
)

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className="space-y-3">
    <h4 className="text-base font-semibold">{title}</h4>
    {children}
  </section>
)

/** Гайд подключения ИИ-агента, разбитый на отдельные блоки: адрес, шаги, примеры, вопросы. */
export const ConnectGuide = () => {
  const [client, setClient] = useState<ClientId>('claude')
  const m = useMessages(messages)
  const steps = guideFor(useUiLang()).steps
  const isMobile = useIsMobile()
  // На телефоне все нейросети не влезают в ширину — переключатель прокручивается
  // и гаснет у края, где есть ещё пункты. Там же он стоит НАД карточкой, под
  // подзаголовком (вкладки к её содержимому); на ПК — внутри карточки, как было.
  const picker = (
    <ScrollFadeRow className="-mx-1 px-1">
      <div className="w-max">
        <SegmentedControl<ClientId>
          value={client}
          onChange={setClient}
          options={CLIENTS.map((item) => ({ ...item, icon: CLIENT_ICONS[item.id] }))}
        />
      </div>
    </ScrollFadeRow>
  )

  return (
    <TooltipProvider>
      <div className="space-y-8">
        <div className="flex items-center gap-8 rounded-2xl bg-foreground/5 p-8">
          <img
            src={butterfly}
            alt=""
            aria-hidden="true"
            draggable={false}
            className="hidden size-32 shrink-0 sm:block"
          />
          <div className="min-w-0 flex-1 space-y-6">
            <GuideHeading />
            <CopyField value={MCP_URL} mono />
          </div>
        </div>

        <Section title={m.howToConnect}>
          {isMobile ? picker : null}
          <div className="space-y-6 rounded-2xl bg-foreground/5 p-8">
            {isMobile ? null : picker}
            {/* У Claude шаги живут в окне «Пошаговый гайд», у Claude Code — готовая
                просьба в чат (как у Higgsfield); у остальных — шаги. */}
            {client === 'claude' ? (
              <ClaudeConnectButton />
            ) : client === 'code' ? (
              <ClaudeCodePromptCard />
            ) : (
              <ol className="space-y-5">
                {steps[client].map((step, index) => (
                  <li key={step.title} className="flex gap-3">
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-brand/20 text-sm font-semibold text-brand">
                      {index + 1}
                    </span>
                    <div className="min-w-0 flex-1 space-y-2">
                      <p className="text-base">{step.title}</p>
                      {step.copy ? <CopyField value={step.copy} mono /> : null}
                      {step.link ? <StepLink {...step.link} /> : null}
                    </div>
                  </li>
                ))}
              </ol>
            )}
          </div>
        </Section>

        <Section title={m.whatToWrite}>
          <PromptCards />
        </Section>

        <Section title={m.faq}>
          <Faq />
        </Section>
      </div>
    </TooltipProvider>
  )
}
